import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * On the product page, always increases by exactly 1.
   * In the cart drawer/cart page, keeps Shopify's original step behavior.
   *
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    const quantityInput = this.refs.quantityInput;
    const isCartQuantity = quantityInput.hasAttribute('data-cart-line');

    if (isCartQuantity) {
      quantityInput.stepUp();
    } else {
      const currentValue = Number(quantityInput.value) || 1;
      const maxValue =
        quantityInput.max !== '' && !Number.isNaN(Number(quantityInput.max))
          ? Number(quantityInput.max)
          : Infinity;

      quantityInput.value = String(Math.min(currentValue + 1, maxValue));
    }

    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * On the product page, always decreases by exactly 1.
   * In the cart drawer/cart page, keeps Shopify's original step behavior.
   *
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    const quantityInput = this.refs.quantityInput;
    const isCartQuantity = quantityInput.hasAttribute('data-cart-line');

    if (isCartQuantity) {
      quantityInput.stepDown();
    } else {
      const currentValue = Number(quantityInput.value) || 1;
      const minValue =
        quantityInput.min !== '' && !Number.isNaN(Number(quantityInput.min))
          ? Number(quantityInput.min)
          : 1;

      quantityInput.value = String(Math.max(currentValue - 1, minValue));
    }

    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   *
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;

    if (
      !(event.target instanceof HTMLInputElement) ||
      document.activeElement !== quantityInput
    ) {
      return;
    }

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   *
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }

    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();

    const parsedValue = Number.parseInt(quantityInput.value, 10);
    const newValue = Number.isNaN(parsedValue) ? 1 : parsedValue;

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        newValue,
        Number(quantityInput.dataset.cartLine)
      )
    );
  }

  /**
   * Checks that minimum and maximum quantity rules are met.
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;

    const currentValue = Number(quantityInput.value);
    const minValue =
      quantityInput.min !== '' && !Number.isNaN(Number(quantityInput.min))
        ? Number(quantityInput.min)
        : null;

    const maxValue =
      quantityInput.max !== '' && !Number.isNaN(Number(quantityInput.max))
        ? Number(quantityInput.max)
        : null;

    if (Number.isNaN(currentValue)) {
      quantityInput.value = String(minValue ?? 1);
      return;
    }

    if (minValue !== null && currentValue < minValue) {
      quantityInput.value = String(minValue);
    }

    if (maxValue !== null && currentValue > maxValue) {
      quantityInput.value = String(maxValue);
    }
  };

  /**
   * Gets the quantity input.
   *
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing <input ref="quantityInput" /> inside <quantity-selector-component />'
      );
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define(
    'quantity-selector-component',
    QuantitySelectorComponent
  );
}